import React, {memo, useEffect, useRef, useState} from "react";
import {useDispatch, useSelector} from "react-redux";

//components:
import {NetgraphNavbarBadges, CustomTooltip, ImageUploadModal} from "../../widgets";
import {Button, CircularProgress, TextField} from "@material-ui/core";
import Autocomplete from "@material-ui/lab/Autocomplete";
import EditIcon from "@material-ui/icons/Edit";
import CloseIcon from "@material-ui/icons/Close";

//helpers:
import {useTranslation} from "react-i18next";

//redux:
import {reviewActions} from "../../redux/Review/reviewRedux";
import {mediaItemPageActions} from "../../redux/MediaItemPage/mediaItemRedux";
import {editPerson, toggleBadge, toggleDeletePerson} from "../../redux/Review/reviewCrud";
import {uploadImage} from "../../redux/SpeakersList/speakersListCrud";
import {loadUserParameter} from "../../redux_slices/contextSlice";

const BadgesBoard = ({page, videoId, insightsOptions, setSelectedInsights, selectedInsights}) => {
    const {t} = useTranslation();

    const [computedBadges, setComputedBadges] = useState(null);
    const [badgesToggling, setBadgesToggling] = useState([]);
    const [faces, setFaces] = useState(null);
    const [errorMessage, setErrorMessage] = useState("");
    const [placeholderUploadImg, setPlaceholderUploadImg] = useState("");
    const [openUploadImageModal, setOpenUploadImageModal] = useState({
        open: false,
        type: "",
    });
    const [speakerId, setSpeakerId] = useState(null);

    const personRef = useRef(null);

    //redux store:
    const dispatch = useDispatch();
    const getBadges = () => dispatch(reviewActions.getBadges(videoId, language || "en-US"));
    const getVideoNetgraphData = () =>
        dispatch(mediaItemPageActions.getVideoNetgraphData(videoId, page, language || "en-US"));

    const {badges = [], peopleDropdown = []} = useSelector(state => state.review);
    const language = useSelector(state => loadUserParameter(state, "language"));

    const isDocument = useSelector(state => state.mediaItemPage?.info?.type_id === 2);
    const showSpeakersBadge = page === "review" && !isDocument;

    const setErrorMessageTemp = message => {
        setErrorMessage(message);
        setTimeout(() => {
            setErrorMessage("");
        }, 3000);
    };

    useEffect(() => {
        getBadges();
    }, []);

    useEffect(() => {
        if (selectedInsights && selectedInsights[0] && selectedInsights[0].name) {
            if (badges && badges.length) {
                setComputedBadges(null);
                setFaces(null);
                if (selectedInsights[0].key === "speakers") {
                    const speakersObj = badges.find(obj => obj.type === "people");
                    if (speakersObj && speakersObj.people) {
                        setFaces(speakersObj.people);
                    }
                } else {
                    const selectedDataObj = badges.find(obj => obj.type === selectedInsights[0].key);

                    const selectedObjBadges = selectedDataObj && selectedDataObj.badges;

                    const sortedBadges =
                        selectedObjBadges &&
                        selectedObjBadges.length &&
                        selectedObjBadges.sort((a, b) => {
                            if (a.name < b.name) return -1;
                            if (a.name > b.name) return 1;
                            return 0;
                        });

                    setComputedBadges(sortedBadges);
                }
            }
        } else {
            setSelectedInsights([
                {
                    key: "speakers",
                    name: "Speakers",
                    disabled: 0,
                },
            ]);
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedInsights, badges]);

    const toggleBadges = badge => {
        toggleBadge(videoId, badge).then(({result}) => {
            if (result === "success") {
                getBadges();
                getVideoNetgraphData().then(() => {
                    setTimeout(() => {
                        const removeBadgeIdToggled = badgesToggling.filter(_badge => _badge === badge);
                        setBadgesToggling([...removeBadgeIdToggled]);
                    }, 500);
                });
            } else {
                setErrorMessageTemp("There was a problem editing this badge!");
            }
        });
    };

    const togglePerson = async personId => {
        const toggled = await toggleDeletePerson(videoId, personId);

        if (toggled.result === "success") {
            getBadges();
            getVideoNetgraphData();
        } else {
            setErrorMessageTemp("There was a problem changing this speaker visibility!");
        }
    };

    const uploadHandler = ({id, file, onConfirmUploaded, onError}) => {
        if (id && file) {
            const onSuccess = () => {
                getBadges();
                getVideoNetgraphData();
                onConfirmUploaded();
            };
            uploadImage({id, file, onSuccess, onError});
        }
    };

    const onPersonChange = async ({e, value, speaker}) => {
        if (value) {
            const personId = speaker.person_id;
            const personData = {name: value.name || value};

            const {result} = await editPerson(videoId, personId, personData);

            if (result && result === "success") {
                getBadges();
                getVideoNetgraphData();
            } else {
                setErrorMessageTemp("There was a problem editing this speaker!");
            }
        }
        personRef.current && personRef.current.blur();
    };

    return (
        <div className="row min-w-100">
            <div className="col-12">
                <div className="card card-custom card-stretch category-network-chart-container gutter-b">
                    <div className="card-header w-100 flex-wrap border-bottom border-gray">
                        <div className="d-flex align-items-center">
                            <span className="mr-3">{t("REVIEW.BADGESBOARD.TITLE")}</span>
                            <NetgraphNavbarBadges
                                showSpeakersBadge={showSpeakersBadge}
                                insightsOptions={insightsOptions}
                                selectedInsights={selectedInsights}
                                setSelectedInsights={setSelectedInsights}
                            />
                        </div>
                    </div>
                    <div className="position-relative">
                        <div className="card-body">
                            <span className="text-danger my-2 mb-10">{errorMessage || ""}</span>
                            {/* ----------BADGES--------- */}
                            {computedBadges && computedBadges.length
                                ? computedBadges.map((badge, i) => (
                                      <div
                                          key={i}
                                          className={`d-inline-flex mr-4 mb-4 bg-white ${
                                              Number(badge.disabled) === 1 ? "removed" : ""
                                          }`}
                                      >
                                          <div className="position-relative">
                                              {badgesToggling.find(_badge => _badge === badge) ? (
                                                  <div className="d-flex justify-content-center align-items-center position-absolute top-0 left-0 w-100 h-100 z-index-3">
                                                      <CircularProgress />
                                                  </div>
                                              ) : null}
                                              <Button
                                                  className="reviewpage-badgesboard-badge"
                                                  variant="outlined"
                                                  size="small"
                                                  color="secondary"
                                                  onClick={() => {
                                                      toggleBadges(badge);
                                                      setBadgesToggling([...badgesToggling, badge]);
                                                  }}
                                              >
                                                  <div>
                                                      {badge.name ? <span>{badge.name}</span> : null}
                                                      {badge.speaker ? <span>{`(${badge.speaker})`}</span> : null}
                                                  </div>
                                              </Button>
                                          </div>
                                      </div>
                                  ))
                                : null}

                            {/* ----------SPEAKERS--------- */}
                            {faces && faces.length
                                ? faces.map((person, i) => (
                                      <div key={i} className={"d-flex flex-column"}>
                                          <div
                                              className={`d-flex align-items-center py-2 px-4 justify-content-between bg-white rounded person ${
                                                  person.disabled ? "removed-person" : ""
                                              }`}
                                          >
                                              <div className="position-relative w-60 h-60 mr-5">
                                                  <div
                                                      className="w-60px h-60px rounded-circle border border-gray shadow bg_image_cover"
                                                      style={{
                                                          backgroundImage: `url(${person.image_url ||
                                                              "/media/avatars/dummy-avatar.jpg"})`,
                                                      }}
                                                  />
                                                  <CustomTooltip
                                                      config={{
                                                          text: "Edit speaker image",
                                                          placement: "top",
                                                      }}
                                                  >
                                                      <button
                                                          onClick={() => {
                                                              setPlaceholderUploadImg(person.image_url);
                                                              setSpeakerId(person.person_id);
                                                              setOpenUploadImageModal({
                                                                  open: true,
                                                                  type: "",
                                                              });
                                                          }}
                                                          disabled={!!person.disabled}
                                                          className="btn btn-icon btn-light position-absolute bottom-0 right-0 edit_button"
                                                      >
                                                          <EditIcon />
                                                      </button>
                                                  </CustomTooltip>
                                              </div>

                                              <div
                                                  className={`d-flex flex-column justify-content-center flex-grow-1 my-lg-0 my-2 min-h-60px max-w-300px mr-auto position-relative`}
                                              >
                                                  <Autocomplete
                                                      multiple={false}
                                                      filterSelectedOptions
                                                      options={peopleDropdown}
                                                      defaultValue={person}
                                                      value={person}
                                                      disabled={!!person.disabled}
                                                      freeSolo
                                                      blurOnSelect
                                                      size={"small"}
                                                      getOptionLabel={option =>
                                                          option && option.name ? option.name : ""
                                                      }
                                                      // includeInputInList
                                                      onChange={(e, value) => {
                                                          onPersonChange({e, value, speaker: person});
                                                      }}
                                                      renderInput={(params, i) => (
                                                          <TextField
                                                              {...params}
                                                              key={i}
                                                              ref={personRef}
                                                              className="py-2 overflow-hidden w-100 d-flex justify-content-between align-items-center"
                                                              variant="outlined"
                                                              disabled={!!person.disabled}
                                                              label=""
                                                              type="text"
                                                              placeholder={"Create or select a speaker"}
                                                          />
                                                      )}
                                                  />
                                              </div>

                                              <button
                                                  type="button"
                                                  className="close ml-2 btn btn-primary p-4 rounded-circle d-flex justify-content-center align-items-center"
                                                  data-dismiss="modal"
                                                  aria-label="Close"
                                                  onClick={() => togglePerson(person.person_id)}
                                              >
                                                  <CloseIcon fontSize="large" />
                                              </button>
                                          </div>
                                      </div>
                                  ))
                                : null}
                        </div>
                    </div>
                </div>
            </div>
            <ImageUploadModal
                id={`${speakerId}`}
                onUpload={uploadHandler}
                open={openUploadImageModal}
                setOpen={setOpenUploadImageModal}
                placeholder={placeholderUploadImg}
            />
        </div>
    );
};

export default memo(
    BadgesBoard,
    (prevProps, nextProps) =>
        prevProps.page === nextProps.page &&
        prevProps.videoId === nextProps.videoId &&
        prevProps.insightsOptions === nextProps.insightsOptions &&
        prevProps.setSelectedInsights === nextProps.setSelectedInsights &&
        prevProps.selectedInsights === nextProps.selectedInsights
);
